import axios from 'axios';
import { API } from '../App';
import {
  addToSyncQueue,
  getSyncQueue,
  removeFromSyncQueue,
  addConflict,
  cacheData,
  getCachedData,
  isOnline
} from './offlineStorage';

// Sync manager for handling offline/online data synchronization
class SyncManager {
  constructor() {
    this.isSyncing = false;
    this.listeners = [];
    this.setupOnlineListener();
  }

  // Subscribe to sync events
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  // Notify listeners
  notify(event, data) {
    this.listeners.forEach(l => l(event, data));
  }

  // Setup online/offline listeners
  setupOnlineListener() {
    window.addEventListener('online', () => {
      console.log('Back online - starting sync');
      this.notify('online', null);
      this.syncAll();
    });

    window.addEventListener('offline', () => {
      console.log('Gone offline');
      this.notify('offline', null);
    });
  }

  // Fetch with offline fallback
  async fetchWithOffline(endpoint, type) {
    if (isOnline()) {
      try {
        const response = await axios.get(`${API}${endpoint}`);
        await cacheData(type, response.data);
        return { data: response.data, fromCache: false };
      } catch (error) {
        console.error(`Failed to fetch ${endpoint}, trying cache`, error);
        const cached = await getCachedData(type);
        if (cached) {
          return { data: cached, fromCache: true };
        }
        throw error;
      }
    } else {
      const cached = await getCachedData(type);
      if (cached) {
        return { data: cached, fromCache: true };
      }
      throw new Error('Offline und keine gecachten Daten verfügbar');
    }
  }

  // Queue action for sync (when offline)
  async queueAction(action) {
    const entry = await addToSyncQueue(action);
    this.notify('queued', entry);
    return entry;
  }

  // Execute action (online) or queue (offline)
  async executeOrQueue(action) {
    if (isOnline()) {
      try {
        const result = await this.executeAction(action);
        return { success: true, result, queued: false };
      } catch (error) {
        // If online but request fails, queue it
        if (error.response?.status >= 500 || error.code === 'ECONNABORTED') {
          const entry = await this.queueAction(action);
          return { success: false, queued: true, entry };
        }
        throw error;
      }
    } else {
      const entry = await this.queueAction(action);
      return { success: false, queued: true, entry };
    }
  }

  // Execute a single action
  async executeAction(action) {
    const { method, endpoint, data } = action;
    
    switch (method) {
      case 'POST':
        return await axios.post(`${API}${endpoint}`, data);
      case 'PUT':
        return await axios.put(`${API}${endpoint}`, data);
      case 'DELETE':
        return await axios.delete(`${API}${endpoint}`);
      default:
        throw new Error(`Unknown method: ${method}`);
    }
  }

  // Sync all queued actions
  async syncAll() {
    if (this.isSyncing || !isOnline()) return;
    
    this.isSyncing = true;
    this.notify('syncStart', null);
    
    const queue = await getSyncQueue();
    let synced = 0;
    let failed = 0;
    let conflicts = 0;

    for (const item of queue) {
      try {
        await this.executeAction(item);
        await removeFromSyncQueue(item.id);
        synced++;
        this.notify('itemSynced', item);
      } catch (error) {
        if (error.response?.status === 409) {
          // Conflict detected
          await addConflict({
            action: item,
            serverError: error.response?.data,
            type: 'conflict'
          });
          await removeFromSyncQueue(item.id);
          conflicts++;
          this.notify('conflict', item);
        } else if (error.response?.status === 404) {
          // Resource no longer exists
          await addConflict({
            action: item,
            serverError: error.response?.data,
            type: 'not_found'
          });
          await removeFromSyncQueue(item.id);
          conflicts++;
        } else {
          // Retry later
          item.retries = (item.retries || 0) + 1;
          if (item.retries >= 3) {
            await addConflict({
              action: item,
              serverError: error.response?.data || error.message,
              type: 'failed'
            });
            await removeFromSyncQueue(item.id);
            conflicts++;
          }
          failed++;
        }
      }
    }

    this.isSyncing = false;
    this.notify('syncComplete', { synced, failed, conflicts });
    
    return { synced, failed, conflicts };
  }

  // Get pending sync count
  async getPendingCount() {
    const queue = await getSyncQueue();
    return queue.length;
  }
}

// Singleton instance
export const syncManager = new SyncManager();

// Offline-aware API functions
export const offlineApi = {
  // Products
  async getProducts() {
    return syncManager.fetchWithOffline('/products', 'products');
  },

  // Compartments
  async getCompartments() {
    return syncManager.fetchWithOffline('/compartments', 'compartments');
  },

  // Inventory
  async getInventory() {
    return syncManager.fetchWithOffline('/inventory', 'inventory');
  },

  // Refills
  async getRefills() {
    return syncManager.fetchWithOffline('/refills?limit=50', 'refills');
  },

  // Create refill (with offline support)
  async createRefill(data) {
    return syncManager.executeOrQueue({
      method: 'POST',
      endpoint: '/refills',
      data,
      type: 'refill',
      description: `Auffüllung: ${data.product_name} in Fach ${data.slot_id}`
    });
  },

  // Undo refill
  async undoRefill(id, description) {
    return syncManager.executeOrQueue({
      method: 'DELETE',
      endpoint: `/refills/${id}`,
      type: 'refill_undo',
      description: description || 'Auffüllung rückgängig'
    });
  },

  // Cash balance
  async getCashBalance() {
    return syncManager.fetchWithOffline('/cash/balance', 'cashBalance');
  },

  // Cash transactions
  async getCashTransactions() {
    return syncManager.fetchWithOffline('/cash/transactions?limit=100', 'transactions');
  },

  // Create cash transaction
  async createCashTransaction(data) {
    return syncManager.executeOrQueue({
      method: 'POST',
      endpoint: '/cash/transactions',
      data,
      type: 'cash_transaction',
      description: `${data.transaction_type}: ${data.amount}€`
    });
  },

  // Validate cash
  async validateCash(data) {
    return syncManager.executeOrQueue({
      method: 'POST',
      endpoint: '/cash/validate',
      data,
      type: 'cash_validation',
      description: `Kassenvalidierung: ${data.actual_amount}€`
    });
  },

  // Update coin container
  async updateCoinContainer(data) {
    return syncManager.executeOrQueue({
      method: 'PUT',
      endpoint: '/cash/coin-container',
      data,
      type: 'coin_container',
      description: `Münzbehälter: ${data.amount}€`
    });
  },

  // Update compartment
  async updateCompartment(id, data) {
    return syncManager.executeOrQueue({
      method: 'PUT',
      endpoint: `/compartments/${id}`,
      data,
      type: 'compartment_update',
      description: `Fach aktualisiert`
    });
  },

  // Update inventory
  async updateInventory(productId, data) {
    return syncManager.executeOrQueue({
      method: 'PUT',
      endpoint: `/inventory/${productId}`,
      data,
      type: 'inventory_update',
      description: `Lager: ${data.action} ${data.quantity}`
    });
  }
};

export default syncManager;
